<template>
  <div>
    <van-overlay
      :show="innerShow"
      :z-index="2000"
      :lock-scroll="true"
      style="top: 2.75rem; height: calc(100% - 2.75rem);"
      @click="handleOverlayClick"
    >
      <div class="drawer-panel" @click.stop>
        <div class="header" @click="goHeader">
          <template v-if="isLoggedIn">
            <van-image
              v-if="avatarUrl"
              class="avatar"
              round
              width="32"
              height="32"
              :src="avatarUrl"
            />
            <van-icon v-else name="manager" size="24" color="#1E3C3E" />
            <span class="header-text">{{ nickName || 'User' }}</span>
          </template>
          <template v-else>
            <van-icon name="manager" size="24" color="#1E3C3E" />
            <span class="header-text">Log In/Sign Up</span>
          </template>
        </div>
        <div class="menu">
          <div
            v-for="item in menuList"
            :key="item.key"
            class="menu-item"
            :class="{ active: isActive(item) }"
            @click="handleClick(item)"
          >
            {{ item.text }}
          </div>
          <div v-if="isLoggedIn" class="menu-item danger" @click="logout">Log out</div>
        </div>
      </div>
    </van-overlay>
    <!-- Terms of Service/Privacy Policy -->
    <van-popup
      v-model="showAgreementPopup"
      round
      closeable
      position="bottom"
      :style="{ height: '85%' }"
    >
      <div class="agreement-panel">
        <div class="agreement-title">{{ agreementTitle }}</div>
        <div v-if="agreementLoading" class="agreement-loading">Loading...</div>
        <div v-else class="agreement-content" v-html="agreementHtml"></div>
      </div>
    </van-popup>
  </div>
</template>
<script>
import { Dialog, Toast } from 'vant'
import { getToken } from '@/utils/storage'
export default {
  name: 'UserDrawer',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      currentUserInfo: null,
      showAgreementPopup: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementHtml: '',
      agreementsCache: {
        service_terms: '',
        privacy_policy: ''
      },
      menuList: [
        { key: 'home', text: 'Home', path: '/' },
        { key: 'order', text: 'Orders', path: '/order' },
        { key: 'after', text: 'After-sales', path: '/sales' },
        { key: 'favorite', text: 'Favorites', path: '/myCollection' },
        // 新增：My 礼品卡s（位于Favorites下面）
        { key: 'myGiftCard', text: 'My Gift Cards', path: '/myGiftCard' },
        { key: 'account', text: 'Account & Settings', path: '/account' },
        { key: 'comment', text: 'Reviews', path: '/comment' },
        { key: 'terms', text: 'Terms of Service', action: 'service_terms' },
        { key: 'privacy', text: 'Privacy Policy', action: 'privacy_policy' },
        { key: 'merchantEntry', text: 'Merchant Onboarding', path: '/shopSettled' }
      ]
    }
  },
  computed: {
    innerShow: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      }
    },
    isLoggedIn() {
      return !!getToken()
    },
    nickName() {
      return (this.currentUserInfo && this.currentUserInfo.nickName) || ''
    },
    avatarUrl() {
      return (this.currentUserInfo && this.currentUserInfo.avatarUrl) || ''
    }
  },
  watch: {
    // 抽屉打开时同步一次用户信息（优先缓存，不足则请求）
    innerShow(val) {
      if (val) this.loadCurrentUserInfo()
    }
  },
  mounted() {
    // 预加载一次，避免首次打开闪烁
    this.loadCurrentUserInfo()
  },
  methods: {
    handleOverlayClick() {
      // 打开协议弹窗时，避免点击遮罩误关闭抽屉
      if (this.showAgreementPopup) return
      this.innerShow = false
    },
    loadCurrentUserInfo() {
      // 先读本地缓存
      try {
        const cached = localStorage.getItem('userInfo')
        if (cached) this.currentUserInfo = JSON.parse(cached)
      } catch (e) {
        // ignore
      }
      // 未登录则不请求
      if (!this.isLoggedIn) return
      // 登录态：补拉一次最新用户信息
      this.$api
        .getUserDetail()
        .then(res => {
          if (res && res.code === 1 && res.data) {
            this.currentUserInfo = res.data.userInfo || {}
            try {
              localStorage.setItem('userInfo', JSON.stringify(this.currentUserInfo || {}))
              localStorage.setItem('userDetail', JSON.stringify(res.data || {}))
            } catch (e) {
              // ignore
            }
          }
        })
        .catch(() => {})
    },
    isActive(item) {
      return item.path && this.$route && this.$route.path === item.path
    },
    goHeader() {
      this.$router.push(this.isLoggedIn ? '/account' : '/login').catch(() => {})
      this.innerShow = false
    },
    handleClick(item) {
      if (item.path) {
        this.$router.push(item.path).catch(() => {})
        this.innerShow = false
      } else {
        if (item.action === 'service_terms' || item.action === 'privacy_policy') {
          this.openAgreement(item.action)
          return
        }
        Toast && Toast('Feature in development')
      }
    },
    async openAgreement(type) {
      this.agreementTitle = type === 'service_terms' ? 'Terms of Service' : 'Privacy Policy'
      this.showAgreementPopup = true
      // 先用缓存，秒开
      const cached = this.agreementsCache[type]
      if (cached) {
        this.agreementHtml = cached
        return
      }
      this.agreementLoading = true
      try {
        // 接口：GET /api/user.user/register（返回 service_terms/privacy_policy）
        // 后端要求 multipart/form-data 的话，这里依然会带上 app_id/token 的 query 参数（由 request 拦截器统一注入）
        const res = await this.$api.getRegisterAgreements({ app_id: 10001 })
        if (res && res.code === 1 && res.data) {
          const serviceTerms = res.data.service_terms && res.data.service_terms.service_terms
          const privacyPolicy = res.data.privacy_policy && res.data.privacy_policy.privacy_policy
          this.agreementsCache.service_terms = serviceTerms || ''
          this.agreementsCache.privacy_policy = privacyPolicy || ''
          this.agreementHtml = this.agreementsCache[type] || ''
          if (!this.agreementHtml) {
            Toast && Toast('No content')
          }
        } else {
          Toast && Toast((res && res.msg) ? res.msg : 'Failed to get data')
        }
      } catch (e) {
        // request 拦截器可能已提示，这里不重复 toast
      } finally {
        this.agreementLoading = false
      }
    },
    logout() {
      Dialog.confirm({ title: 'Log out', message: 'Are you sure you want to log out?' })
        .then(() => {
          this.$store.dispatch('LoginOut')
          this.currentUserInfo = null
          // 如果当前页本身需要登录，退出后回到首页，避免停留在受保护页触发各种“请先登录”
          if (this.$route && this.$route.meta && this.$route.meta.requiresAuth) {
            this.$router.replace('/').catch(() => {})
          }
          this.innerShow = false
        })
        .catch(() => {})
    }
  }
}
</script>
<style lang="scss" scoped>
.drawer-panel {
  position: fixed;
  top: 2.75rem;
  left: 0;
  width: 65%;
  height: calc(100% - 2.75rem);
  background: #fff;
  box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}
.header {
  display: flex;
  align-items: center;
  padding: 1rem;
  height: 3.25rem;
  box-sizing: border-box;
  .avatar{
    flex: 0 0 auto;
  }
  .header-text {
    margin-left: 0.5rem;
    color: #1e3c3e;
    font-weight: 600;
  }
}
.menu {
  padding: 0 0.5rem;
}
.menu-item {
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #eeeeee;
  box-sizing: border-box;
  font-size: 0.9375rem;
  color: #333;
}
.menu-item.active {
  background: #eaf5ef;
}
.menu-item.danger {
  color: #666;
}
.agreement-panel{
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.agreement-title{
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  padding: 0 3rem;
  box-sizing: border-box;
}
.agreement-loading{
  padding: 1rem;
  color: #666;
}
.agreement-content{
  flex: 1 1 auto;
  overflow: auto;
  padding: 1rem;
  box-sizing: border-box;
  color: #333;
  line-height: 1.6;
  word-break: break-word;
}
.agreement-content ::v-deep img{
  max-width: 100%;
  height: auto;
  display: block;
}
</style>
